import { useInfiniteQuery } from '@tanstack/react-query';

import { resolve } from 'api/resolve';

import { createNextOffsetGetter } from '../offsetPaging';
import { useProjectNodesApi } from '../project-nodes-api';
import type { NodeFilter } from '../project-nodes-api';

/** Matches the backend `PaginatedRequest` default/max (1–100). */
export const DATA_NODES_PAGE_SIZE = 50;

export const PROJECT_NODES_BY_TYPE_QUERY_KEY = 'projectNodesByType';

/**
 * The identifying prefix of every `useDataNodesByType` cache entry for a node type, regardless of
 * sort/filter variant (those trail the key — see the full `queryKey` below). Callers that need to
 * patch or invalidate every cached page-list of a node type (e.g. an in-cell edit) should match on
 * this prefix rather than duplicating the literal key.
 */
export function getDataNodesByTypeQueryKeyPrefix(
  projectId: string | undefined,
  technicalName: string | undefined,
  proposalId: string | undefined
) {
  return [PROJECT_NODES_BY_TYPE_QUERY_KEY, projectId, technicalName, proposalId] as const;
}

/**
 * Next-offset rule for the paginated node listing. Shares its rule with `getNextConnectedNodesOffset`
 * for the connected-nodes listing (see `createNextOffsetGetter`); only the page size differs.
 */
export const getNextNodesOffset = createNextOffsetGetter(DATA_NODES_PAGE_SIZE);

interface UseDataNodesByTypeOptions {
  /** Only fetch when the group is expanded. */
  enabled?: boolean;
  /** Property to sort by. Unsorted (backend default order) when omitted. */
  orderBy?: string;
  orderDirection?: 'Asc' | 'Desc';
  /** Server-side column filters, AND-combined. No filtering when empty/omitted. */
  filters?: NodeFilter[];
}

/**
 * Lazily list data node instances of a blueprint (template) node type, paginated.
 * `getNextPageParam` returns the next offset until a short page signals the end.
 */
export function useDataNodesByType(
  technicalName: string | undefined,
  projectId: string | undefined,
  proposalId: string | undefined,
  { enabled = true, orderBy, orderDirection, filters }: UseDataNodesByTypeOptions = {}
) {
  const { getDataNodesByType } = useProjectNodesApi();

  return useInfiniteQuery({
    queryKey: [
      ...getDataNodesByTypeQueryKeyPrefix(projectId, technicalName, proposalId),
      orderBy,
      orderDirection,
      filters
    ],
    queryFn: ({ pageParam }) => {
      if (!projectId || !technicalName) {
        return [];
      }
      return resolve(
        getDataNodesByType(projectId, technicalName, {
          offset: pageParam,
          limit: DATA_NODES_PAGE_SIZE,
          proposalId,
          orderBy,
          orderDirection,
          filters
        })
      );
    },
    initialPageParam: 0,
    getNextPageParam: getNextNodesOffset,
    enabled: Boolean(projectId) && Boolean(technicalName) && enabled
  });
}
